import { cmp, factor, isNegative, isZero, money, scaleOf, } from '@fameacc/money';
import { z } from 'zod';
import { customerTypeSchema, ibanMitPruefzifferSchema, provenanceSchema, vatIdWellformed, } from './master-data.js';
import { logoQuelleSchema, rechtsformSchema } from './einstellungen.js';
import { centAmount, isoDate, nonNegativeAmount, uuid } from './posting.js';
import { POSITIONSTEXT_MARKUP_UNZULAESSIG, pruefePositionstext } from './positionstext.js';
import { kundenportalEntscheidungArtSchema, kundenportalEreignisArtSchema, } from './portal-arten.js';
/**
 * Die Verträge des Belegwesens — Beleg, Position, Steuersatzgruppe, Ausstellung,
 * Storno, Gutschrift, Lückenbegründung und Rechnungssteller.
 *
 * **Was hier geschieht, ist Normalisierung und Meldung, nicht Durchsetzung.**
 * Die maßgeblichen Prüfungen stehen als `CHECK` in Migration 0022 —
 * `document_line_text`, `document_line_item`, `document_line_tax_rate_range`,
 * `document_line_quantity_positive` — und gelten auch gegen direktes SQL. Was
 * hier steht, bringt die Eingabe in die Form, die die Datenbank erwartet, und
 * liefert eine verständliche Meldung, bevor die Datenbank eine unverständliche
 * liefert. Dieselbe Arbeitsteilung wie bei `assertBalanced` im Buchungspfad
 * (Phase-1-Spec 6.2).
 *
 * **Jede Normalisierung steht in einer `.transform()` mit `ctx` und `z.NEVER`,
 * niemals in einer `.superRefine()`.** Deren Rückgabewert ist als `void`
 * typisiert und wird zur Laufzeit verworfen: Ein damit gebautes Schema gäbe die
 * **rohe** Eingabe zurück, `'10.0000'` bliebe `'10.0000'` und ginge
 * unkanonisiert in die Datenbank, obwohl die Prüfung grün war.
 *
 * **Die Bausteine kommen aus `posting.ts` und werden nicht nachgebaut:**
 * `nonNegativeAmount`, `isoDate`, `uuid`. Zwei Fassungen desselben Musters
 * wären zwei Auslegungen.
 */
// --- Zwei neue Bausteine ---------------------------------------------------
/**
 * Ein Einzelpreis: bis zu VIER Nachkommastellen, jedes Vorzeichen.
 *
 * `centAmount` wäre hier falsch. Der Einzelpreis ist nicht der Rundungspunkt —
 * der ist die Position (Spec 4.2) —, und Konzept 4.2 verlangt ausdrücklich vier
 * Stellen. Der Altbestand nutzt sie nicht, aber die brutto verankerten
 * Positionen speichern ihren Anker mit zwei Stellen, und der 15-stellige double
 * wandert gar nicht erst ins neue Modell (Spec 3.5).
 *
 * Das Vorzeichen ist erlaubt und ausdrücklich nicht der Fehlerfall: Der Rabatt
 * ist eine Position mit negativem Einzelpreis und kein eigenes Feld (Spec 2,
 * 3.4). Ein zweites Rabattfeld wäre ein zweiter Rundungspunkt.
 *
 * Alles in EINER `.transform()` mit `ctx` und `z.NEVER`.
 */
export const unitPriceAmount = z.string().transform((wert, ctx) => {
    let betrag;
    try {
        betrag = money(wert);
    }
    catch {
        // Der Text der MoneyParseError beschreibt die kanonische Form und gehört
        // nicht in eine HTTP-Antwort.
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kein gültiger Einzelpreis' });
        return z.NEVER;
    }
    // `numeric(15,4)` rundet den Wert, bevor irgendein CHECK ihn sieht. Die
    // Skalenprüfung ist deshalb die einzige Prüfung dieses Bausteins ohne
    // Rückhalt in der Datenbank — dieselbe Lage wie bei `centAmount`.
    if (scaleOf(betrag) > 4) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: 'AMOUNT_SCALE_INVALID' },
            message: 'Höchstens vier Nachkommastellen im Einzelpreis zulässig',
        });
        return z.NEVER;
    }
    return betrag;
});
/**
 * Ein Steuersatz als FAKTOR: 0 <= r < 1.
 *
 * `< 1` und nicht `<= 1` (Spec 7.3): Ein Steuersatz von 100 % existiert nicht,
 * und ein versehentlich eingetragenes `19` wird damit sofort abgelehnt statt
 * eine Rechnung über den zwanzigfachen Betrag zu erzeugen.
 *
 * Dass `r < 1` gilt, ist zugleich die Zusage, auf die sich die Bruttorichtung
 * der Arithmetik stützt: Der Divisor `1 + r` ist damit nie null.
 */
export const taxRateFactor = z.string().transform((wert, ctx) => {
    let satz;
    try {
        satz = factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kein gültiger Steuersatz' });
        return z.NEVER;
    }
    // Verglichen wird DEZIMAL über `packages/money` und niemals über `Number`:
    // `Number(satz)` wäre eine Fliesskommaumwandlung an einer Stelle, an der die
    // Grenze die Zusage ist — `no-float-money` untersagt es zu Recht. Der Umweg
    // über `Money` ist verlustfrei: `Factor` und `Money` sind beide `string`, und
    // `0.19` ist derselbe Dezimalwert, gleich unter welcher Marke er steht.
    const alsBetrag = money(satz);
    if (isNegative(alsBetrag)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Der Steuersatz darf nicht negativ sein' });
        return z.NEVER;
    }
    if (cmp(alsBetrag, money('1')) >= 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Der Steuersatz ist ein Faktor und kleiner als 1 — 19 % sind 0.19 und nicht 19',
        });
        return z.NEVER;
    }
    return satz;
});
/** Eine Menge: Faktor, echt über null. Das Vorzeichen steckt im Preis. */
const quantityFactor = z.string().transform((wert, ctx) => {
    let menge;
    try {
        menge = factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Keine gültige Menge' });
        return z.NEVER;
    }
    const alsBetrag = money(menge);
    if (isNegative(alsBetrag) || isZero(alsBetrag)) {
        // document_line_quantity_positive. Das Vorzeichen steckt im Preis, nicht in
        // der Menge — so hält es der Altbestand (Spec 3.4), und zwei Orte für
        // dasselbe Vorzeichen sind einer zu viel.
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Die Menge muss größer als null sein' });
        return z.NEVER;
    }
    return menge;
});
/** Trimmt und zieht Folgen von Leerraum auf ein Leerzeichen zusammen. */
function straffen(wert) {
    return wert.trim().replace(/\s+/g, ' ');
}
/** Ein Pflichttextfeld. Leer heißt Ablehnung, nicht Platzhalter. */
const pflichtText = (feld) => z.string().transform((wert, ctx) => {
    const gestrafft = straffen(wert);
    if (gestrafft === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
        return z.NEVER;
    }
    return gestrafft;
});
/**
 * Ein Pflichttext, der **unverändert** durchgereicht wird — nur für Felder,
 * in denen Weissraum Inhalt ist.
 *
 * **Es gibt genau einen solchen: den Rumpf der Versandmail** (`text` in
 * `documentVersandInputSchema`). Gefunden von `C-mailversandnaht`
 * (2026-09-03) beim Bau der Naht zwischen Vorlagen und Versand: `pflichtText`
 * zieht **jede** Folge von Leerraum auf ein Leerzeichen zusammen, also auch
 * Zeilenumbrüche — der mehrzeilige Text mit Anrede, Absatz und Grussformel
 * kam beim Empfänger seit `V-mailversand` als **eine** durchlaufende Zeile an.
 * Kein Test sah das, weil keiner mit einem mehrzeiligen Rumpf prüfte.
 *
 * Dieselbe Unterscheidung und dieselbe Begründung trifft
 * `packages/contracts/src/mailvorlagen.ts` schon für `koerperHtml`
 * («Weissraum ist dort Inhalt»); die Naht macht die zwei Verträge jetzt zu
 * zwei Enden derselben Strecke, und ein gestraffter Rumpf hier hiesse, dass
 * `document_versand.koerper_text` (Migration `0151`) etwas anderes aufbewahrt
 * als die Vorlage gesagt hat.
 *
 * Geprüft wird weiterhin, dass überhaupt etwas dasteht.
 */
const roherPflichtText = (feld) => z.string().superRefine((wert, ctx) => {
    if (wert.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
    }
});
/** Ein optionales Textfeld: getrimmt, zusammengezogen, leer wird `null`. */
const optionalText = z
    .string()
    .optional()
    .nullable()
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const gestrafft = straffen(wert);
    return gestrafft === '' ? null : gestrafft;
});
/** Dasselbe als Patch-Feld: `undefined` heißt „nicht ändern", `null` heißt „leeren". */
const optionalPatchText = z
    .union([z.string(), z.null()])
    .optional()
    .transform((wert) => {
    if (wert === undefined)
        return undefined;
    if (wert === null)
        return null;
    const gestrafft = straffen(wert);
    return gestrafft === '' ? null : gestrafft;
});
/** Ein optionaler Kalendertag im Änderungssatz. */
const optionalPatchDate = z
    .union([isoDate, z.null()])
    .optional()
    .transform((wert) => wert);
// --- Aufzählungen ---------------------------------------------------------
/**
 * Die elf Belegarten, die dieses System AUSSTELLT (Migration `0019`).
 *
 * **Die drei Archivbelegarten aus `0119` stehen ausdrücklich NICHT hier**,
 * sondern in `archiveDocumentTypeSchema`. Diese Aufzählung ist die
 * EINGABEseite: Sie trägt `documentInputSchema.type`, und was hier steht, ist
 * über `POST /api/v1/documents` anlegbar. Ein Archivbeleg entsteht
 * ausschliesslich über `POST /api/v1/invoices/import`; er brächte seine
 * historische Nummer mit, und `DocumentNumberService.allocate()` weist ihn
 * ohnehin ab (`E-639`).
 *
 * Sie trägt ausserdem die beiden Tafeln des Vorlagenregisters
 * (`TEMPLATE_BY_TYPE`, `CAPTIONS` in `apps/api/src/render/template-registry.ts`)
 * als `Record<DocumentType, …>` — und genau deren Vollständigkeit soll für die
 * Archivklasse NICHT gelten: Für sie gibt es nichts zu rendern, das Stück liegt
 * im Archiv.
 */
export const documentTypeSchema = z.enum([
    'quote',
    'order_confirmation',
    'invoice',
    'partial_invoice',
    'final_invoice',
    'recurring_invoice',
    'credit_note',
    'cancellation',
    'proforma',
    'delivery_note',
    'dunning',
]);
/**
 * Die drei Archivbelegarten des Altrechnungen-Imports (Migration `0119`,
 * `D-1`).
 *
 * Sie tragen **keine** Zeile in `document_type_range` und sind damit für die
 * Prüfungen 4a/4b aus `assert_document_consistent()` baulich unsichtbar — das
 * ist der Kern von `D-1` und kein vergessener Eintrag
 * (`document_type_range_kein_archivtyp` hält es fest). Der benannte Preis
 * dieser Entscheidung ist, dass jeder Dienst, der pauschal über `document`
 * läuft, eine ausdrückliche Regel für sie braucht; welche das je Dienst ist,
 * steht in `docs-site/content/architecture/legacy-import.md`.
 */
export const archiveDocumentTypeSchema = z.enum([
    'legacy_invoice',
    'legacy_cancellation',
    'legacy_credit_note',
]);
/**
 * Alle vierzehn Belegarten — die AUSGABEseite.
 *
 * **Warum die Trennung in zwei Aufzählungen und nicht eine.** Ein Archivbeleg
 * muss in der Belegliste sichtbar sein (er ist der Nachweis über 8.555
 * Ausgangsbelege), und er darf nicht anlegbar sein. Eine einzige Aufzählung
 * könnte nur eines von beiden: Sie trüge entweder die Liste nicht oder die
 * Anlage zu weit. `documentSummarySchema.type` und der Filter der Liste lesen
 * deshalb diese Aufzählung, `documentInputSchema.type` die elf oben.
 *
 * Abgeleitet und nicht abgeschrieben: Eine zwölfte ausstellbare Belegart darf
 * nicht an zwei Stellen nachgetragen werden müssen.
 */
export const anyDocumentTypeSchema = z.enum([
    ...documentTypeSchema.options,
    ...archiveDocumentTypeSchema.options,
]);
/** Ist diese Belegart eine Archivbelegart? Das TypeScript-Gegenstück zu `ist_archivbelegtyp()` (0119). */
export function istArchivbelegart(art) {
    return archiveDocumentTypeSchema.options.includes(art);
}
/**
 * Der übernommene Zahlungsstand einer Archivbelegart, benannt aus dem
 * Zahlencode des Altsystems (`document.legacy_status`, Migration `0119`,
 * `D-2`) — Auftrag `AI-zahlstatusanzeige`.
 *
 * **Fünf Werte tragen heute eine Bedeutung**: `-1` Fehler, `1` unbezahlt, `4`
 * storniert, `5` bezahlt — wörtlich aus der Verteilung, die Spec
 * `docs/superpowers/specs/2026-09-01-altimport-design.md` Abschnitt 3.1 am
 * Dump vom 15.08.2026 nachzählt —, und seit `AI-gutschriftstatus`
 * (2026-09-05) `2` **erledigt**. `0` und `3` sind an der Spalte weiterhin
 * ausdrücklich ZUGELASSEN (`document_legacy_status_range`, Bereich `-1..5`)
 * — ein zweiter Dump kann sie tragen —, aber **keiner** von ihnen kommt heute
 * vor und **keiner** hat eine benannte Bedeutung.
 *
 * **`erledigt` (Code `2`) — Nachtrag `AI-gutschriftstatus` (2026-09-05),
 * Owner-Entscheidung unter benannter Annahme.** `AI-stornoaltstatus` fand am
 * selben Tag, dass eine ausgeführte (verschickte) Alt-Gutschrift
 * (`fa_gutschrift.status = 1`, alle 5 echten Sätze) keinem der vier
 * ursprünglich benannten Codes entspricht — sie ist nicht bezahlt (eine
 * Aussage über eine RECHNUNG), nicht storniert (sie hebt selbst nichts auf)
 * und nicht offen (sie ist bereits erledigt) — und blieb deshalb als
 * `altstatus_unbekannt` blockierender Befund. Der Controller hat den bis
 * dahin unbenannten, aber an der Spalte längst zugelassenen Code `2` für
 * genau diesen Fall benannt: ein ausgeführter Beleg, der weder Forderung
 * (offen/bezahlt) noch Aufhebung (storniert) ist. `altstatusFuer('gutschrift',
 * 1)` bildet seither auf `2` ab (siehe dort); `fa_storno`-Code `2`
 * (ausgeführter Storno) bleibt unverändert bei `4` (storniert) — zwei
 * verschiedene Domänenwerte derselben Spalte, zwei verschiedene Ziele.
 *
 * **`unbenannt` ist die sechste, ANZEIGESEITIGE Ausprägung — Nachtrag nach
 * einem echten Torabwurf (Zug 141, 2026-09-04).** Die SCHREIBSEITE
 * (`altimportBelegSchema.altstatus`, `packages/contracts/src/altimport.ts`)
 * lässt seither nur noch die benannten Codes durch; ein anderer Wert wird
 * beim Zubringer zur blockierenden Fehllistenzeile (`altstatus_unbekannt`,
 * `apps/api/src/cutover/rechnungszubringer.ts::liesAltstatus`) und nie zu
 * einem geschriebenen `legacy_status`. Trotzdem bleibt die LESESEITE robust
 * gegen einen fremden Code, den eine Zeile schon trägt — sei es aus der Zeit
 * vor dieser Sperre oder aus einem Weg, der an ihr vorbeigeschrieben hat: Ein
 * Lesepfad, der auf gespeicherte Daten mit einem Wurf antwortet, reisst die
 * GANZE Belegliste mit sich (genau das ist am 2026-09-04 passiert, mit
 * `altstatus: 2` in zwei Testdateien, die noch die alte, breitere
 * Schreibseite kannten). `legacyStatusZuAnzeige` bleibt eine werfende, reine
 * Funktion — sie trägt `E-821` und bleibt die scharfe Prüfung, dass jeder
 * bekannte Code benannt wird. `legacyStatusAnzeigeOderUnbenannt` ist die
 * NICHT werfende Variante daneben, für einen Lesepfad über echten Bestand;
 * `document.query.ts::toSummary` ruft sie und nicht die werfende Fassung.
 *
 * `unbenannt` ist ausdrücklich NICHT dasselbe wie `zahlwegSchema.unbekannt`
 * in `offene-posten.ts` — dessen fünfter Wert steht für einen KÜNFTIGEN
 * `bank_provider`, den diese Datei einfach noch nicht kennt, und ist damit
 * ein SCHREIBSEITIG erreichbarer Wert. `unbenannt` ist nie ein Wert, den ein
 * Aufrufer absichtlich schreibt — die Schreibseite lässt ihn seit diesem
 * Nachtrag gar nicht mehr entstehen — sondern die Auskunft „diese Zeile trägt
 * einen Code, den die Anzeige nicht kennt", an einem Datenbestand, den die
 * Anwendung nicht rückwirkend bereinigt.
 */
export const legacyDocumentStatusSchema = z.enum([
    'bezahlt',
    'unbezahlt',
    'storniert',
    'fehler',
    'erledigt',
    'unbenannt',
]);
/**
 * Die fünf benannten Codes, wörtlich aus `fa_rechnungen.status` (`D-2`) plus
 * `2` „erledigt" (`AI-gutschriftstatus`, 2026-09-05, siehe Kopfkommentar von
 * {@link legacyDocumentStatusSchema}).
 */
const LEGACY_STATUS_CODES = new Map([
    [-1, 'fehler'],
    [1, 'unbezahlt'],
    [2, 'erledigt'],
    [4, 'storniert'],
    [5, 'bezahlt'],
]);
/**
 * Ist `code` einer der vier benannten Codes? Die EINE Prüfung für die
 * Schreibseite (`altimportBelegSchema.altstatus`,
 * `rechnungszubringer.ts::liesAltstatus`) — dieselbe Liste, aus der
 * `legacyStatusZuAnzeige` liest, damit Schreib- und Leseseite nie
 * auseinanderlaufen.
 */
export function istBenannterAltstatusCode(code) {
    return LEGACY_STATUS_CODES.has(code);
}
/**
 * Benennt `document.legacy_status` — die EINE Abbildung, reine Funktion, ohne
 * Netzwerk und ohne Datenbank.
 *
 * **Wirft für jeden Code ausserhalb der vier benannten.** „Rohzahl daneben?
 * nein, nur die benannte Form" (Auftrag `AI-zahlstatusanzeige`) heisst: Ein
 * Beleg zeigt entweder eine der vier benannten Ausprägungen oder gar keine
 * Antwort — nie eine Zahl, deren Bedeutung diese Datei nicht kennt. Ein
 * unbenannter Wert ist ein Befund und keine Anzeige, die eine Bedeutung
 * vortäuscht (`E-821`).
 *
 * **Für einen Lesepfad über echten Bestand ist das die FALSCHE Funktion —
 * siehe `legacyStatusAnzeigeOderUnbenannt` daneben.** Diese Fassung bleibt
 * werfend, weil `E-821` genau das prüft; sie ist absichtlich NICHT die
 * Funktion, die `document.query.ts` beim Lesen einer beliebigen Zeile ruft.
 */
export function legacyStatusZuAnzeige(code) {
    const anzeige = LEGACY_STATUS_CODES.get(code);
    if (anzeige === undefined) {
        throw new Error(`Unbenannter Altsystem-Zahlungsstand ${String(code)}: nur -1 (Fehler), 1 (unbezahlt), ` +
            '2 (erledigt), 4 (storniert) und 5 (bezahlt) sind benannt (document.legacy_status, ' +
            'Migration 0119, D-2; 2 seit AI-gutschriftstatus, 2026-09-05).');
    }
    return anzeige;
}
/**
 * Dieselbe Abbildung wie `legacyStatusZuAnzeige`, aber NICHT werfend —
 * `'unbenannt'` statt einer Ausnahme (Nachtrag Zug 141, 2026-09-04).
 *
 * **Für wen das die richtige Funktion ist.** Jeder Lesepfad über
 * gespeicherten Bestand — `document.query.ts::toSummary` ist der einzige
 * Aufrufer heute — darf auf eine gültige, aber fremd benannte Zeile nicht mit
 * einem Wurf antworten: Eine einzige Zeile mit einem unbenannten Code risse
 * sonst die ganze Belegliste/-ansicht mit sich, obwohl 999 andere Zeilen
 * vollständig lesbar wären. `legacyStatusZuAnzeige` bleibt daneben stehen,
 * weil `E-821` genau IHRE werfende Schärfe prüft — diese Funktion ist der
 * Umweg darum herum, nicht ihr Ersatz.
 */
export function legacyStatusAnzeigeOderUnbenannt(code) {
    try {
        return legacyStatusZuAnzeige(code);
    }
    catch {
        return 'unbenannt';
    }
}
/** Die Preisbasis. Der Kern dieser Phase. */
export const priceBasisSchema = z.enum(['net', 'gross']);
/**
 * Die Steuerszenarien: die sechs aus Konzept 8 und seit Migration `0258`
 * (`PF-kuszenario`, Spec Systemeinstellungen 3.2, `D-11`) der
 * Kleinunternehmer nach § 19 UStG. `kleinunternehmer` wird nicht nach dem Ort
 * der Leistung gewählt, sondern folgt der Steuerfassung am
 * Leistungsstichtag — die Ableitung (`leiteSzenarioAb`) liefert ihn für
 * Inland und EU-Privatkunden unter der Schwelle, sobald die Fassung den
 * Status `kleinunternehmer` trägt.
 */
export const taxScenarioSchema = z.enum([
    'domestic',
    'eu_b2b_reverse_charge',
    'eu_b2c_oss',
    'eu_b2c_domestic_vat',
    'third_country_b2c',
    'third_country_b2b',
    'kleinunternehmer',
]);
/** Sachposition oder Textzeile. 41 % der Altpositionen sind Text (Spec 3.4). */
export const documentLineTypeSchema = z.enum(['item', 'text']);
/** Der Lebenszyklus eines Belegs. Drei Werte, nicht sieben (Spec 8.3). */
export const documentStatusSchema = z.enum(['draft', 'issued', 'cancelled']);
/**
 * Der VEREINTE Anzeigezustand eines Belegs — die eine Spalte «Zustand», die
 * ein regulaerer Beleg und ein importierter Archivbeleg gleichermassen
 * tragen (Auftraggeber-Entscheidung 2026-09-05, Auftrag `AI-zustandvereint`).
 *
 * **Der Anlass, woertlich.** „Ich finde es nicht gut, dass es jetzt noch in
 * der Tabelle bei den Belegen ‚Altstatus' gibt. Das kann doch im gleichen
 * angezeigt werden wie die normalen Rechnungen auch. Wir haben das Feld
 * ‚Zustand' — warum zeigen wir nicht bei Zustand auch den Status an wie
 * Storniert, Bezahlt, Offen etc." `AI-zahlstatusanzeige` (Zug 141) hatte den
 * uebernommenen Altstatus in eine EIGENE Spalte gestellt; zwei Spalten, von
 * denen je nach Belegherkunft immer genau eine leer ist, sind fuer den Leser
 * zwei Fragen statt einer Antwort.
 *
 * **Rein ABGELEITET, keine Spalte in der Datenbank.** Der Wert entsteht in
 * `belegzustandAus()` aus `document.status` (Lebenszyklus) und
 * `document.legacy_status` (uebernommener Zahlungsstand des Altsystems,
 * Migration `0119`, `D-2`) und wird nirgends gespeichert. Das ist dieselbe
 * Erwaegung, aus der der Zahlstand eines Belegs aus dem Journal abgeleitet
 * und nie an den Beleg geschrieben wird: Ein gespeicherter Anzeigezustand
 * waere eine zweite Wahrheit, die beim ersten Storno auseinanderliefe.
 *
 * **`bezahlt` ist heute NUR aus dem Altbestand erreichbar — das ist die
 * benannte Grenze dieser Aufzaehlung.** Fuer einen Beleg, den DIESES System
 * ausgestellt hat, gibt es heute keine Tabelle, die eine Zahlung einem
 * Ausgangsbeleg zuordnet (gemessen 2026-09-05 ueber
 * `information_schema.columns` nach `document_id`/`invoice_id`: nur
 * `journal_entry`, `aborechnung_lauf` und die `incoming_*`-Tabellen tragen
 * eine solche Spalte, keine davon ist eine Zahlungszuordnung zum
 * Ausgangsbeleg). Ein regulaerer, ausgestellter Beleg ist deshalb `offen`,
 * bis die Offene-Posten-/Mahnwesen-Aera
 * (`docs/superpowers/specs/2026-08-19-mahnwesen-design.md`) diese Zuordnung
 * baut. Der Wert steht trotzdem SCHON in dieser Aufzaehlung, damit die
 * Oberflaeche spaeter nicht wieder umgebaut werden muss: Es kommt dann eine
 * Zeile in `belegzustandAus()` dazu und keine neue Spalte, kein neues
 * Abzeichen und kein neuer Filterwert.
 *
 * **`fehler` ist ausdruecklich KEIN Fehler DIESES Systems**, sondern die
 * Auskunft „das Altsystem hat diesen Beleg als fehlerhaft gefuehrt" (Code
 * `-1`) beziehungsweise „diese Zeile traegt einen Altcode, den die Anzeige
 * nicht benennen kann" (`unbenannt`, siehe
 * `legacyStatusAnzeigeOderUnbenannt`). Beides ist derselbe Befund fuer den
 * Leser: hier stimmt am uebernommenen Bestand etwas nicht.
 *
 * **`erledigt` ist der sechste, seit `AI-gutschriftstatus` (2026-09-05)
 * hinzugekommene Wert.** Eine AUSGEFUEHRTE Alt-Gutschrift
 * (`fa_gutschrift.status = 1`, uebernommen als `legacyStatus: 'erledigt'`)
 * ist weder Forderung (`offen`/`bezahlt`) noch Aufhebung (`storniert`) --
 * ein eigener, neutraler Endzustand. Ein Storno DIESES Systems schlaegt auch
 * ihn: Dieselbe Reihenfolge wie bei jedem anderen Altzustand gilt hier
 * unveraendert.
 */
export const belegzustandSchema = z.enum([
    'entwurf',
    'offen',
    'bezahlt',
    'storniert',
    'fehler',
    'erledigt',
]);
/**
 * Die EINE Ableitung des vereinten Anzeigezustands — reine Funktion, ohne
 * Netzwerk und ohne Datenbank (`E-838`).
 *
 * **Ein Storno in DIESEM System schlaegt jeden Altstatus.** Ein Archivbeleg,
 * den der Stornozubringer hier auf `status = 'cancelled'` gesetzt hat, ist
 * storniert — auch wenn das Altsystem ihn noch als `bezahlt` fuehrte. Die
 * Reihenfolge der Zweige unten ist deshalb keine Bequemlichkeit, sondern die
 * Entscheidung: Was dieses System ueber den Beleg WEISS, geht dem vor, was
 * das Altsystem ueber ihn festgehalten HAT.
 *
 * **Jede Kombination ist benannt, keine faellt durch.** Drei
 * Lebenszykluswerte mal sechs Altzustaende (fuenf benannte plus `null`) —
 * `E-838` zaehlt sie einzeln durch, damit ein sechster Altzustand oder ein
 * vierter Lebenszykluswert hier ein Typfehler wird und keine stille Zelle.
 */
export function belegzustandAus(beleg) {
    // Was dieses System weiss, geht vor: ein hier stornierter Beleg ist
    // storniert, unabhaengig davon, was das Altsystem fuehrte.
    if (beleg.status === 'cancelled')
        return 'storniert';
    if (beleg.legacyStatus !== null) {
        switch (beleg.legacyStatus) {
            case 'bezahlt':
                return 'bezahlt';
            case 'unbezahlt':
                return 'offen';
            case 'storniert':
                return 'storniert';
            case 'erledigt':
                return 'erledigt';
            case 'fehler':
            case 'unbenannt':
                // Ein unbenannter Altcode ist fuer den Leser derselbe Befund wie ein
                // ausdruecklich fehlerhafter: am uebernommenen Bestand stimmt etwas
                // nicht. Eine sechste Auspraegung „unbenannt" in der Zustandsspalte
                // waere eine Auskunft ueber das Innenleben des Imports und keine
                // ueber den Beleg.
                return 'fehler';
        }
    }
    // Kein Archivbeleg: allein der Lebenszyklus. `cancelled` ist oben
    // abgefangen, `issued` ist heute `offen` (siehe die Grenze in
    // `belegzustandSchema`).
    return beleg.status === 'draft' ? 'entwurf' : 'offen';
}
/**
 * Derselbe Zustand als SQL-Ausdruck, fuer den serverseitigen Listenfilter
 * (`documentQuerySchema.belegzustand`, `document.query.ts::listDocuments`).
 *
 * **Erzeugt und nicht abgeschrieben.** Jeder Zweig unten holt seinen Wert aus
 * `belegzustandAus()` beziehungsweise `LEGACY_STATUS_CODES` — eine zweite,
 * von Hand gepflegte `CASE`-Kaskade waere genau die Fehlerklasse, gegen die
 * dieses Projekt sonst ueberall Waechter stellt: Sie liefe beim naechsten
 * neuen Zustandswert still auseinander, und der Filter faende dann etwas
 * anderes als die Spalte zeigt. `E-838` haelt beide Seiten gegeneinander.
 *
 * Die Spaltennamen kommen ausschliesslich vom Aufrufer und nie aus einer
 * Anfrage; die eingesetzten Werte sind Zeichenketten aus `belegzustandSchema`
 * und Zahlen aus `LEGACY_STATUS_CODES`, beide aus dieser Datei.
 */
export function belegzustandSqlAusdruck(statusSpalte, legacySpalte) {
    const wert = (zustand) => `'${zustand}'`;
    const zweige = [
        `WHEN ${statusSpalte} = 'cancelled' THEN ${wert(belegzustandAus({ status: 'cancelled', legacyStatus: null }))}`,
    ];
    for (const status of ['draft', 'issued']) {
        zweige.push(`WHEN ${legacySpalte} IS NULL AND ${statusSpalte} = '${status}' THEN ${wert(belegzustandAus({ status, legacyStatus: null }))}`);
    }
    for (const [code, anzeige] of LEGACY_STATUS_CODES) {
        zweige.push(`WHEN ${legacySpalte} = ${String(code)} THEN ${wert(belegzustandAus({ status: 'issued', legacyStatus: anzeige }))}`);
    }
    // Der ELSE-Zweig ist der gespeicherte, unbenannte Altcode — dieselbe
    // Antwort, die `legacyStatusAnzeigeOderUnbenannt` der Anzeige gibt.
    return `CASE ${zweige.join(' ')} ELSE ${wert(belegzustandAus({ status: 'issued', legacyStatus: 'unbenannt' }))} END`;
}
// --- Die Position ---------------------------------------------------------
/**
 * Ein Positionstext: `pflichtText` **unverändert** (`D-14` der Positionstext-Spec —
 * der Umbruch bleibt `<br>`, nicht U+000A), mit der Erfassungsprüfung aus
 * `positionstext.ts` als `superRefine` daran gehängt (Auftrag `PT-vertrag`,
 * Task 2 des Plans
 * `docs/superpowers/plans/2026-09-04-positionstext-auszeichnung-umsetzung.md`).
 *
 * Jeder Befund von `pruefePositionstext` wird zu einem eigenen Zod-Issue mit
 * `params: { code: POSITIONSTEXT_MARKUP_UNZULAESSIG, tag }` (`D-15`) — derselbe
 * Weg wie bei `DATENREGEL_FEHLER` in `datenregeln.ts`: Der fachliche Code reitet
 * auf dem `params`-Feld eines `custom`-Befundes mit, `codeFromIssues()`
 * (`apps/api/src/common/validation.ts`) liest ihn dort und nicht aus dem Text.
 *
 * **Wird hier definiert und nicht in `positionstext.ts`.** Jene Datei trägt
 * ausdrücklich keine Abhängigkeit, auch nicht `zod` — sie läuft im Browser und
 * im Dienst wortgleich (`D-13`). Der Zod-Baustein braucht `pflichtText`, das
 * lokal zu dieser Datei gehört, und wird von `aborechnungen.ts` importiert statt
 * dort nachgebaut — derselbe Weg wie bei `unitPriceAmount`/`taxRateFactor`.
 *
 * **Gilt nur an den zwei Positionsverträgen** (Spec 5.3, `D-3`):
 * `documentLineInputObject.description` unten und
 * `aborechnungPositionInputObject.description`. **Nicht** an
 * `altimportPositionSchema.beschreibung` (`packages/contracts/src/altimport.ts`)
 * — der Import ist kein Erfassungsweg, sondern die Übernahme eines Bestands, den
 * `D-4` zu ändern verbietet, und benutzt seinen eigenen, unveränderten
 * `pflichtText`.
 */
export const positionstext = (feld) => pflichtText(feld).superRefine((wert, ctx) => {
    for (const befund of pruefePositionstext(wert)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: befund.meldung,
            params: { code: POSITIONSTEXT_MARKUP_UNZULAESSIG, tag: befund.tag },
        });
    }
});
/**
 * Das Objektschema einer Position, **vor** der Querprüfung zwischen ihren
 * Feldern.
 *
 * Getrennt gehalten, weil `.strict()` auf dem Objekt sitzt und die Querprüfung
 * eine `.transform()` darüber ist: `z.object(...).strict().transform(...)` ist
 * die einzige Reihenfolge, in der beides gilt.
 */
const documentLineInputObject = z
    .object({
    lineType: documentLineTypeSchema.default('item'),
    description: positionstext('description'),
    quantity: quantityFactor.optional().nullable(),
    unitCode: optionalText,
    unitPrice: unitPriceAmount.optional().nullable(),
    taxRate: taxRateFactor.optional().nullable(),
    taxKey: optionalText,
    revenueAccountNumber: optionalText,
    servicePeriodStart: isoDate.optional().nullable(),
    servicePeriodEnd: isoDate.optional().nullable(),
})
    .strict();
/**
 * Eine Belegposition.
 *
 * **Die Querbedingungen zwischen den Feldern stehen hier und nicht nur in der
 * Datenbank.** `line_type = 'text'` verträgt keine Menge, `'item'` verlangt
 * Menge, Einheit, Satz, Schlüssel und Konto. Beides zugleich zu prüfen ist hier
 * ausdrücklich richtig und kein Widerspruch zur Regel „eine Wahrheit": Die
 * maßgebliche Prüfung ist `document_line_text`/`document_line_item` in der
 * Datenbank, das Schema liefert die **Meldung**.
 */
export const documentLineInputSchema = documentLineInputObject.transform((wert, ctx) => {
    const normalisiert = {
        ...wert,
        quantity: wert.quantity ?? null,
        unitPrice: wert.unitPrice ?? null,
        taxRate: wert.taxRate ?? null,
        servicePeriodStart: wert.servicePeriodStart ?? null,
        servicePeriodEnd: wert.servicePeriodEnd ?? null,
    };
    if (normalisiert.lineType === 'text') {
        const besetzt = [
            ['quantity', normalisiert.quantity],
            ['unitCode', normalisiert.unitCode],
            ['unitPrice', normalisiert.unitPrice],
            ['taxRate', normalisiert.taxRate],
            ['taxKey', normalisiert.taxKey],
            ['revenueAccountNumber', normalisiert.revenueAccountNumber],
        ].filter(([, v]) => v !== null);
        if (besetzt.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [besetzt[0]?.[0] ?? 'lineType'],
                message: 'Eine Textzeile trägt weder Menge noch Einheit, Preis, Satz, Schlüssel noch Konto',
            });
            return z.NEVER;
        }
        return normalisiert;
    }
    const fehlend = [
        ['quantity', normalisiert.quantity],
        ['unitCode', normalisiert.unitCode],
        ['unitPrice', normalisiert.unitPrice],
        ['taxRate', normalisiert.taxRate],
        ['taxKey', normalisiert.taxKey],
        ['revenueAccountNumber', normalisiert.revenueAccountNumber],
    ].filter(([, v]) => v === null);
    if (fehlend.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [fehlend[0]?.[0] ?? 'lineType'],
            message: 'Eine Sachposition verlangt Menge, Einheit, Einzelpreis, Steuersatz, ' +
                'Steuerschlüssel und Erlöskonto',
        });
        return z.NEVER;
    }
    if (normalisiert.revenueAccountNumber !== null &&
        !/^[0-9]{4,6}$/.test(normalisiert.revenueAccountNumber)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['revenueAccountNumber'],
            message: 'Kontonummer besteht aus vier bis sechs Ziffern',
        });
        return z.NEVER;
    }
    if (normalisiert.servicePeriodStart !== null &&
        normalisiert.servicePeriodEnd !== null &&
        normalisiert.servicePeriodEnd < normalisiert.servicePeriodStart) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['servicePeriodEnd'],
            message: 'Das Ende des Leistungszeitraums liegt vor seinem Beginn',
        });
        return z.NEVER;
    }
    return normalisiert;
});
// --- Der Beleg ------------------------------------------------------------
const documentInputObject = z
    .object({
    type: documentTypeSchema,
    customerId: uuid,
    priceBasis: priceBasisSchema,
    taxScenario: taxScenarioSchema,
    // Die Währung wird großgeschrieben und **nicht** auf 'EUR' festgelegt: Die
    // Durchsetzung ist der Fremdschlüssel auf `currency`, der genau eine Zeile
    // führt (Fall E-165). Ein `z.literal('EUR')` hier nähme ihm seinen
    // erreichbaren Weg.
    currency: z
        .string()
        .transform((v) => v.trim().toUpperCase())
        .pipe(z.string().regex(/^[A-Z]{3}$/, 'Währungscode besteht aus drei Buchstaben')),
    // Kein Rückfall auf 'de' und keiner über das Land: Fehlt die Angabe, greift
    // `customer.language`, und fehlt auch die, lehnt der Dienst mit
    // DOCUMENT_LANGUAGE_REQUIRED ab (Spec 8.2). Ein Pflichtfeld hier machte den
    // Rückfall unmöglich.
    language: z.enum(['de', 'en']).optional(),
    serviceDate: isoDate.optional(),
    /**
     * Der Leistungsmonat (`document.service_month`, Migration `0178`).
     *
     * **Ein vollständiges ISO-Datum und kein `JJJJ-MM`** — dieselbe Form wie
     * `incoming_invoice.service_month` seit `0045` und dort in
     * `eingangsrechnungen.ts` seit langem so vertraglich geführt. Die Spalte
     * ist `date`; der Tagesanteil ist für die Ableitung gleichgültig, weil
     * `monatsletzter()` ihn ohnehin verwirft (`leistungsstichtag.ts`). Ein
     * eigener Monatstyp wäre eine zweite Datumsform im Vertrag, die genau
     * eine Spalte trüge.
     *
     * **Eigenes Feld und keine Konvention:** Ein als Monatserster in
     * `serviceDate` abgelegter Monat wäre von einem echten Leistungstag nicht
     * mehr zu unterscheiden — und genau diese Unterscheidung entscheidet über
     * die Prüfbarkeit (§ 14 Abs. 4 Nr. 6 UStG lässt den Kalendermonat
     * genügen, nicht mehr).
     */
    serviceMonth: isoDate.optional(),
    dueDate: isoDate.optional(),
    predecessorId: uuid.optional(),
    reversesDocumentId: uuid.optional(),
    sourceSystem: pflichtText('sourceSystem'),
    sourceReference: optionalText,
    lines: z.array(documentLineInputSchema).optional(),
})
    .strict();
/**
 * Ein Beleg, wie ihn `DocumentService.create()` entgegennimmt.
 *
 * `taxScenario` ist Pflicht und hat keine Vorgabe: Eine Vorgabe wäre eine
 * Behauptung über die Besteuerung eines Kunden, und Phase 3 hat keine Grundlage
 * für eine solche Behauptung (Spec 8.2). `tax_scenario_source` setzt der Dienst
 * ausnahmslos auf `'declared'` — der Aufrufer kann es nicht angeben, und
 * deshalb steht es nicht in diesem Schema.
 */
export const documentInputSchema = documentInputObject.transform((wert, ctx) => {
    // document_reversal_type bindet `reverses_document_id IS NOT NULL` an
    // `type = 'cancellation'`, in BEIDE Richtungen. Der Storno entsteht ohnehin
    // über POST …/cancel; wer ihn hier von Hand baut, bekommt die Meldung und
    // nicht den Constraint.
    if ((wert.reversesDocumentId !== undefined) !== (wert.type === 'cancellation')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reversesDocumentId'],
            message: 'reversesDocumentId gehört ausschließlich und zwingend zu type cancellation',
        });
        return z.NEVER;
    }
    return wert;
});
/**
 * Der Änderungssatz eines Entwurfs.
 *
 * **Ohne `type`, `customerId` und `priceBasis.`** Die ersten beiden sind
 * unveränderlich — `type` trägt `document_immutable_fields`, der eingefrorene
 * Kundensatz wäre nach einem Wechsel eine Kopie eines anderen Kunden —, und die
 * Preisbasis hat ihren eigenen Vorgang, weil ihr Wechsel eine Neuberechnung
 * jedes Einzelpreises IST (Spec 4.4).
 */
export const documentPatchSchema = z
    .object({
    taxScenario: taxScenarioSchema.optional(),
    language: z.enum(['de', 'en']).optional(),
    serviceDate: optionalPatchDate,
    /** Der Leistungsmonat. `null` löscht ihn — siehe {@link documentInputObject}. */
    serviceMonth: optionalPatchDate,
    dueDate: optionalPatchDate,
    sourceReference: optionalPatchText,
})
    .strict();
/** Die vollständige Ersetzung der Positionsmenge. Es gibt kein `addLine` (Spec 8.2). */
export const documentLinesSchema = z
    .object({
    // Leer ist zulässig: Ein Entwurf ohne Positionen ist ein legitimer
    // Zwischenzustand. Dass ein AUSGESTELLTER wertbehafteter Beleg mindestens
    // eine Sachposition braucht, prüft assert_document_consistent (4a).
    lines: z.array(documentLineInputSchema),
})
    .strict();
/**
 * Der Wechsel der Preisbasis.
 *
 * `mode` ist Pflicht, weil die Umrechnung nicht eindeutig ist: `keep_net` lässt
 * den Nettopreis stehen und ändert den Endpreis, `keep_gross` umgekehrt
 * (Spec 8.2). Eine Vorgabe hier wäre eine stille Entscheidung über die Marge.
 */
export const priceBasisChangeSchema = z
    .object({
    basis: priceBasisSchema,
    mode: z.enum(['keep_net', 'keep_gross']),
})
    .strict();
/**
 * Die Ausstellung.
 *
 * `issueDate` ist optional; fehlt es, nimmt der Dienst `CLOCK.today()`. Beim
 * Storno ist ein **abweichendes** Datum die Ablehnung
 * `DOCUMENT_ISSUE_DATE_FIXED` — und zwar in `issue()` und nicht hier: Ob der
 * Beleg ein Storno ist, weiß erst der Dienst (Spec 8.3, Befund C-2).
 */
export const issueInputSchema = z
    .object({
    issueDate: isoDate.optional(),
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
})
    .strict();
/** Der Storno. Die Begründung ist Pflicht: Ein Storno ohne Grund ist eine Änderung ohne Spur. */
export const cancelInputSchema = z
    .object({
    reason: pflichtText('reason'),
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
})
    .strict();
/**
 * Der Versand eines ausgestellten Belegs per E-Mail (Auftrag `V-mailversand`).
 *
 * **Alle drei Felder sind Pflicht.** Die Weboberfläche belegt sie vor —
 * `empfaenger` aus `customer.email`, `betreff`/`text` mit einer deutschen
 * Vorgabe, die die Belegnummer nennt —, aber der Vertrag verlangt sie explizit
 * und rät nichts selbst. Ein Dienst ohne HTTP-Aufrufer (denkbar ab einer
 * künftigen automatisierten Versandstrecke) bekäme sonst eine stille
 * Vorbelegung, die niemand geprüft hat.
 *
 * `empfaenger` prüft dieselbe Form wie `customer_email_shape`/
 * `vendor_email_shape` (Migration 0014) — `^[^@\s]+@[^@\s]+$` — und **nicht**
 * mehr: Eine strengere Prüfung hier wiese einen Empfänger ab, den die
 * Datenbank für Kunden und Kreditoren längst zulässt.
 *
 * **`betreff` wird gestrafft, `text` nicht** (`C-mailversandnaht`,
 * 2026-09-03). Ein Betreff ist einzeilig; ein Rumpf hat Absätze, und
 * `pflichtText` zog sie bis zu diesem Auftrag zu einer einzigen Zeile
 * zusammen — Begründung bei `roherPflichtText` oben.
 *
 * **Die Weboberfläche belegt `betreff` und `text` seit `C-mailversandnaht`
 * aus der scharfen E-Mail-Vorlage vor** (`GET :id/versand/entwurf`), nicht
 * mehr aus einem Literal im Browser. Der Vertrag verlangt sie trotzdem
 * ausdrücklich und rät weiterhin nichts selbst.
 */
export const documentVersandInputSchema = z
    .object({
    empfaenger: z.string().transform((wert, ctx) => {
        const kanonisch = wert.trim().toLowerCase();
        if (kanonisch === '' || !/^[^@\s]+@[^@\s]+$/.test(kanonisch)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { code: 'EMAIL_INVALID' },
                message: 'E-Mail-Adresse hat eine unzulässige Form',
            });
            return z.NEVER;
        }
        return kanonisch;
    }),
    betreff: pflichtText('betreff'),
    // **Roh und nicht gestrafft** — siehe `roherPflichtText`: Der Betreff ist
    // einzeilig und verträgt das Straffen, der Rumpf verliert dadurch seine
    // Absätze. Der Betreff bleibt deshalb bewusst bei `pflichtText`; die zwei
    // Felder unterscheiden sich hier absichtlich.
    text: roherPflichtText('text'),
})
    .strict();
/**
 * Der **Entwurf** einer Versandmail zu einem Beleg — Auftrag
 * `C-mailversandnaht` (2026-09-03), `MV-7`/`D-18`.
 *
 * Das Ergebnis der Zusammensetzung aus dem Beleg und der **scharfen** Fassung
 * der zu seiner Belegart passenden E-Mail-Vorlage
 * (`apps/api/src/documents/versandentwurf.ts`). Der Versanddialog belegt seine
 * Felder daraus vor, statt einen eigenen Text zu erfinden; derselbe Entwurf
 * entsteht beim tatsächlichen Versand noch einmal auf dem Server und wandert
 * in `document_versand` (Migration `0151`).
 *
 * **`html` ist nullbar, `text` nicht.** Eine Mail geht immer mit einer
 * Textalternative hinaus; der HTML-Rumpf setzt eine scharfe Rahmen- UND
 * Inhaltsfassung voraus, und der Erstbestand aus `0131` deckt nur `de`.
 *
 * **`quelle` und `grund` sind eine Auskunft und keine Warnung.** `rueckfall`
 * heisst: Es gibt für diesen Beleg keine scharfe Vorlage, und der Text kommt
 * aus dem Literal, das bis zu diesem Auftrag im Browser stand. Der Versand ist
 * dann nicht gesperrt — nur unbebildert.
 */
export const documentVersandQuelleSchema = z.enum(['vorlage', 'rueckfall']);
export const documentVersandEntwurfSchema = z.object({
    betreff: z.string(),
    text: z.string(),
    html: z.string().nullable(),
    mailvorlageFassungId: uuid.nullable(),
    quelle: documentVersandQuelleSchema,
    grund: z.string().nullable(),
});
/** Eine Zeile des Versandprotokolls (`document_versand`, Migration 0090). */
export const documentVersandEntrySchema = z.object({
    id: uuid,
    documentId: uuid,
    empfaenger: z.string(),
    betreff: z.string(),
    gesendetAt: z.string(),
    gesendetVon: uuid,
    smtpMessageId: z.string().nullable(),
    fehler: z.string().nullable(),
});
/**
 * Die Antworthülle von `GET /api/v1/documents/:id/versand` — `{ entries: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const documentVersandListResponseSchema = z.object({ entries: z.array(documentVersandEntrySchema).readonly() }).strict();
/**
 * Die Gutschrift.
 *
 * Ihre Positionen werden **frei erfasst** und nicht kopiert (anders als beim
 * Storno): Eine Gutschrift ist eine Teilkorrektur (Spec 8.4). Mindestens eine
 * Position, weil ein wertbehafteter Beleg ohne Sachposition am
 * Konsistenztrigger scheitert (Prüfung 4a) — die Meldung soll vorher kommen.
 */
export const creditNoteInputSchema = z
    .object({
    reason: pflichtText('reason'),
    lines: z.array(documentLineInputSchema).min(1, 'Eine Gutschrift hat mindestens eine Position'),
    serviceDate: isoDate.optional(),
    dueDate: isoDate.optional(),
})
    .strict();
/** Der Versandvermerk. Trägt nichts — er ist ein Zeitpunkt, den der Dienst setzt. */
export const markSentSchema = z.object({}).strict();
// --- Der Lückenbericht ----------------------------------------------------
/**
 * Die Begründung einer Nummernlücke.
 *
 * Ein BEREICH und keine Einzelnummer: Die wahrscheinlichste reale Lücke ist ein
 * Bereich — „Zähler von 11.940 bis 12.098 im Altsystem nie vergeben". Eine
 * Tabelle, die dafür 159 Zeilen verlangt, wird nicht gepflegt (Spec 5.5).
 *
 * Die Zählerstände sind `int` und **kein Geld**: Ein Zählerstand wird gezählt,
 * nicht gerechnet, und `bigint` in JSON ist eine Zahl. Die Obergrenze der
 * Kreise liegt bei 99.999.999 und damit weit unter `Number.MAX_SAFE_INTEGER`.
 */
export const numberGapInputSchema = z
    .object({
    rangeKey: pflichtText('rangeKey'),
    sequenceFrom: z.number().int().positive(),
    sequenceTo: z.number().int().positive(),
    reason: pflichtText('reason'),
    note: optionalText,
})
    .strict()
    .transform((wert, ctx) => {
    // number_range_gap_order: sequence_to >= sequence_from.
    if (wert.sequenceTo < wert.sequenceFrom) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sequenceTo'],
            message: 'Das Ende des Bereichs liegt vor seinem Beginn',
        });
        return z.NEVER;
    }
    return wert;
});
// --- Der Rechnungssteller -------------------------------------------------
/**
 * Eine Bankverbindung des Rechnungsstellers.
 *
 * `iban` kommt aus {@link ibanMitPruefzifferSchema} (Auftrag `LS-ibaneingabe`)
 * — dieselbe Normalisierung und Formprüfung wie am Mandat, am Kreditor und am
 * Bankkonto, hier zusätzlich mit Prüfsummenprüfung am Vertrag.
 */
const issuerBankAccountSchema = z
    .object({
    label: pflichtText('label'),
    iban: ibanMitPruefzifferSchema,
    bic: optionalText,
    bankName: optionalText,
})
    .strict();
/**
 * Die Akzentfarbe des Belegbilds als `#rrggbb` — Kleinbuchstaben, wie die
 * Bedingung `issuer_profile_akzentfarbe_form` (Migration `0256`) sie verlangt.
 * Grossbuchstaben werden kleingeschrieben, alles andere ist ein Formfehler.
 * `undefined` heisst „nicht ändern", `null` (oder leer) „keine Farbe".
 */
const akzentfarbePatch = z
    .union([z.string(), z.null()])
    .optional()
    .transform((wert, ctx) => {
    if (wert === undefined)
        return undefined;
    if (wert === null)
        return null;
    const gestrafft = wert.trim().toLowerCase();
    if (gestrafft === '')
        return null;
    if (!/^#[0-9a-f]{6}$/.test(gestrafft)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Die Akzentfarbe hat die Form #rrggbb.',
        });
        return z.NEVER;
    }
    return gestrafft;
});
/**
 * Die Gestalt einer SEPA-Gläubiger-ID — dieselbe wie die CHECK-Bedingung
 * `issuer_profile_sepa_glaeubiger_id_form` (Migration 0230): Länderkennung,
 * zwei Prüfziffern, drei Zeichen Geschäftsbereich, dann die nationale
 * Kennung. Die Prüfziffern selbst werden nicht nachgerechnet.
 */
const SEPA_GLAEUBIGER_ID_FORM = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{3}[A-Z0-9]{1,28}$/;
const issuerProfileInputObject = z
    .object({
    legalName: pflichtText('legalName'),
    addressLine: pflichtText('addressLine'),
    postalCode: pflichtText('postalCode'),
    city: pflichtText('city'),
    country: pflichtText('country'),
    vatId: z
        .string()
        .optional()
        .nullable()
        .transform((wert, ctx) => {
        if (wert === undefined || wert === null)
            return null;
        const kanonisch = wert.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (kanonisch === '')
            return null;
        if (!vatIdWellformed(kanonisch)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { code: 'VAT_ID_INVALID' },
                message: 'USt-IdNr. hat eine unzulässige Form oder eine falsche Prüfziffer',
            });
            return z.NEVER;
        }
        return kanonisch;
    }),
    taxNumber: optionalText,
    registerCourt: optionalText,
    registerNumber: optionalText,
    managingDirectors: optionalText,
    // Die Komplementärin nach § 177a HGB (Migration 0068) — vier Felder für
    // eine GmbH & Co. KG, alle optional: Nicht jeder Rechnungssteller ist eine.
    generalPartnerName: optionalText,
    generalPartnerSeat: optionalText,
    generalPartnerRegisterCourt: optionalText,
    generalPartnerRegisterNumber: optionalText,
    email: optionalText,
    phone: optionalText,
    website: optionalText,
    logoObjectKey: optionalText,
    // Die Rechtsform (Migration `0256`, Spec Systemeinstellungen 5.1). OPTIONAL:
    // Fehlt sie, schreibt `IssuerService` die Rechtsform der vorigen Fassung fort
    // — eine neue Fassung entsteht in der Regel, weil sich EINE Angabe geändert
    // hat. Gibt es keine vorige Fassung, weist der Dienst ab.
    rechtsform: rechtsformSchema.optional(),
    // Die Antwort auf die Unterfrage „im Handelsregister eingetragen?"
    // (`E-1623`). `null`, wo die Rechtsform sie nicht stellt; fehlt sie, gilt
    // dieselbe Fortschreibung wie bei `rechtsform`, solange die Rechtsform
    // gleich bleibt.
    handelsregisterEingetragen: z.boolean().nullable().optional(),
    // Belegbild der Fassung (`PF-belegbild`, Spec Systemeinstellungen 7, `D-16`).
    // Alle drei OPTIONAL mit demselben Sinn wie `rechtsform`: fehlt die Angabe,
    // gilt die der vorigen Fassung; `null` leert (`agbHinweis`, `akzentfarbe`).
    // `logoQuelle: 'ablage'` verlangt `logoObjectKey` aus dem Logo-Upload
    // (`POST /api/v1/issuer-profiles/logo`).
    agbHinweis: optionalPatchText,
    akzentfarbe: akzentfarbePatch,
    logoQuelle: logoQuelleSchema.optional(),
    /**
     * Die SEPA-Gläubiger-Identifikationsnummer (Migration 0230). **Drei
     * Zustände, und der Unterschied ist der Inhalt dieses Feldes:** nicht
     * genannt (`undefined`) heisst «die der Vorgängerfassung übernehmen» — bei
     * der ersten Fassung «keine»; `null` (oder eine leere Zeichenkette) heisst
     * «ausdrücklich löschen»; ein Wert ersetzt. Ein Feld, dessen Fehlen und
     * dessen Leere dasselbe bedeuteten, machte jede neue Fassung, die es nicht
     * kennt, zu einer Löschung.
     */
    sepaGlaeubigerId: z
        .string()
        .nullable()
        .optional()
        .transform((wert, ctx) => {
        if (wert === undefined || wert === null)
            return wert;
        // Gruppenschreibweise und Kleinschreibung sind Darstellung, nicht Wert.
        const kanonisch = wert.toUpperCase().replace(/\s+/g, '');
        if (kanonisch === '')
            return null;
        if (!SEPA_GLAEUBIGER_ID_FORM.test(kanonisch)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { code: 'SEPA_GLAEUBIGER_ID_INVALID' },
                message: 'Die Gläubiger-ID hat eine unzulässige Form',
            });
            return z.NEVER;
        }
        return kanonisch;
    }),
    validFrom: isoDate,
    bankAccounts: z.array(issuerBankAccountSchema).default([]),
})
    .strict();
/**
 * Eine Fassung des Rechnungsstellers samt Bankverbindungen.
 *
 * § 14 Abs. 4 Nr. 2 UStG verlangt entweder die Steuernummer oder die
 * USt-IdNr. — mindestens eine von beiden. Ein Rechnungssteller ohne beides kann
 * keine Rechnung ausstellen, und `issuer_profile_tax_identity` sagt das in der
 * Datenbank; hier kommt die Meldung.
 */
export const issuerProfileInputSchema = issuerProfileInputObject.transform((wert, ctx) => {
    if (wert.vatId === null && wert.taxNumber === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['taxNumber'],
            message: 'Ein Rechnungssteller braucht eine Steuernummer oder eine USt-IdNr.',
        });
        return z.NEVER;
    }
    return wert;
});
/**
 * Die Antwortverträge des Rechnungsstellers — `V-vertrag3` (2026-08-29).
 *
 * `apps/api/src/issuer/issuer.service.ts` führte `IssuerBankAccountView`/
 * `IssuerProfileView` bislang als eigene Formen; der OpenAPI-Erzeuger blieb an
 * `GET /api/v1/issuer-profiles`, `POST /api/v1/issuer-profiles` und
 * `POST /api/v1/issuer-profiles/:id/new-version` deshalb `[UNGEKLÄRT]`.
 */
/** Eine Bankverbindung des Rechnungsstellers, wie sie nach aussen geht. */
export const issuerBankAccountViewSchema = z
    .object({
    id: uuid,
    label: z.string(),
    iban: z.string(),
    bic: z.string().nullable(),
    bankName: z.string().nullable(),
    /** Zählwert, kein Geld. */
    position: z.number().int().nonnegative(),
})
    .strict();
/**
 * Eine Fassung des Rechnungsstellers, wie sie nach aussen geht.
 *
 * § 14 Abs. 4 Nr. 1 und 2 UStG verlangt den vollständigen Namen und die
 * Anschrift des leistenden Unternehmers sowie seine Steuernummer oder
 * USt-IdNr. Der Beleg friert sie als **Verweis** ein und nicht als Kopie: Die
 * Fassung ist versioniert und unveränderlich, eine Version kann nur beendet,
 * nie geändert werden (Spec 7.3).
 */
export const issuerProfileViewSchema = z
    .object({
    id: uuid,
    legalName: z.string(),
    addressLine: z.string(),
    postalCode: z.string(),
    city: z.string(),
    countryCode: z.string(),
    vatId: z.string().nullable(),
    taxNumber: z.string().nullable(),
    registerCourt: z.string().nullable(),
    registerNumber: z.string().nullable(),
    managingDirectors: z.string().nullable(),
    /**
     * Die Komplementärin nach § 177a HGB — vier Felder, die nur GEMEINSAM eine
     * vollständige Angabe sind (Migration 0068). `null` heisst entweder „keine
     * GmbH & Co. KG" oder „die Angabe steht noch aus".
     */
    generalPartnerName: z.string().nullable(),
    generalPartnerSeat: z.string().nullable(),
    generalPartnerRegisterCourt: z.string().nullable(),
    generalPartnerRegisterNumber: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    website: z.string().nullable(),
    logoObjectKey: z.string().nullable(),
    /** Die Rechtsform der Fassung (Migration `0256`, Spec Systemeinstellungen 5.1). */
    rechtsform: rechtsformSchema,
    /** Antwort auf die Unterfrage Handelsregister; `null`, wo die Rechtsform sie nicht stellt. */
    handelsregisterEingetragen: z.boolean().nullable(),
    /** Adresse der AGB für den Schlusssatz des Angebots; `null` heisst kein Satz (`D-16`). */
    agbHinweis: z.string().nullable(),
    /** Akzentfarbe des Belegbilds als `#rrggbb`; `null` heisst keine (`D-16`). */
    akzentfarbe: z.string().nullable(),
    /** Woher das Logo des Belegs kommt (`D-16`). */
    logoQuelle: logoQuelleSchema,
    /**
     * Die SEPA-Gläubiger-ID dieser Fassung; ohne sie kein Lastschrifteinzug
     * (`LASTSCHRIFT_GLAEUBIGER_ID_FEHLT`).
     */
    sepaGlaeubigerId: z.string().nullable(),
    validFrom: isoDate,
    validTo: isoDate.nullable(),
    bankAccounts: z.array(issuerBankAccountViewSchema).readonly(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/issuer-profiles` — `{ profiles: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const issuerProfileListResponseSchema = z.object({ profiles: z.array(issuerProfileViewSchema).readonly() }).strict();
/**
 * Die zwei Medientypen eines Belegbild-Logos (`PF-belegbild`, `D-16`). PNG und
 * SVG; ein SVG wird vor dem Ablegen auf Skripte und externe Verweise geprüft.
 */
export const logoMedientypSchema = z.enum(['image/png', 'image/svg+xml']);
/** Die Angaben des Logo-Uploads: Der Rumpf IST die Datei, der Medientyp steht im Kopf. */
export const issuerLogoUploadRequestSchema = z.object({ mediaType: logoMedientypSchema }).strict();
/**
 * Antwort des Logo-Uploads. `logoObjectKey` gehört in die nächste Fassung
 * (`logoQuelle: 'ablage'`); bis dahin ändert das Ablegen nichts am Beleg.
 */
export const issuerLogoUploadResultSchema = z
    .object({
    logoObjectKey: z.string(),
    sha256: z.string(),
    mediaType: logoMedientypSchema,
    /** Zählwert, kein Geld. */
    bytes: z.number().int().positive(),
})
    .strict();
// --- Abfragen -------------------------------------------------------------
/**
 * Die Filter der Belegliste.
 *
 * `number` trifft `number` und `billing_display_customer_no` gleichermaßen —
 * der praktische Fall ist ein Kunde am Telefon, der eine der beiden nennt
 * (Spec 8.8).
 */
/**
 * Wonach die Belegliste sortiert werden kann — die zwei Spalten, die eine
 * ORDNUNG tragen und nicht nur einen Text.
 *
 * **Zwei und nicht sieben.** Die Belegtabelle zeigt sieben Spalten; drei davon
 * sind zusammengesetzte Anzeigen (Kunde mit Kundennummer, Zustand mit
 * Zusatzmarke, Zahlung mit Restbetrag) und haben gar keinen einzelnen Wert,
 * nach dem sich ordnen liesse, und der Bruttobetrag ist in diesem Projekt
 * niemals `number` (Hausregel 1) — eine Ordnung über die angezeigte deutsche
 * Schreibweise stellte `9,00` hinter `10,00`. Die Belegart ist eine
 * Aufzählung ohne natürliche Ordnung. Bleiben Datum und Nummer, und genau die
 * zwei hat der Auftraggeber am 2026-09-10 verlangt.
 *
 * **`number` ordnet nicht über die Nummer.** Der Server ordnet über
 * `(number_range_key, number_sequence)`: Innerhalb eines Jahres bricht die
 * Ordnung der Zeichenkette am Stellenwechsel des Zählers, `'20239999' >
 * '202310000'` (Spec 5.3, Fall `E-136`). Der Name des Feldes ist die SPALTE
 * der Oberfläche, nicht die Datenbankspalte.
 */
export const DOKUMENT_SORTIERFELDER = ['issueDate', 'number'];
export const dokumentSortierfeldSchema = z.enum(DOKUMENT_SORTIERFELDER);
/**
 * Die Richtung einer Sortierung.
 *
 * Englisch wie die Feldnamen daneben, und aus demselben Grund wie in
 * `tabellenansicht.ts`: `asc`/`desc` sind die Vokabeln von SQL und von
 * TanStack Table, und eine dritte Schreibweise dafür wäre eine
 * Übersetzungsschicht an jeder Grenze.
 */
export const SORTIERRICHTUNGEN = ['asc', 'desc'];
export const sortierrichtungSchema = z.enum(SORTIERRICHTUNGEN);
export const documentQuerySchema = z
    .object({
    number: optionalText,
    customerId: uuid.optional(),
    // Vierzehn und nicht elf: Nach Archivbelegen zu filtern ist der praktische
    // Weg zu ihnen — sie liegen zu Tausenden zwischen den ausgestellten.
    type: anyDocumentTypeSchema.optional(),
    status: documentStatusSchema.optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    rangeKey: optionalText,
    // Der Filter auf den VEREINTEN Anzeigezustand
    // (`documentSummarySchema.belegzustand`) — Auftrag `AI-zustandvereint`,
    // 2026-09-05. Er ersetzt den vorherigen Filter `legacyStatus`
    // (`AI-zahlstatusanzeige`, Zug 141): Ein Filter, der nur Archivbelege
    // fand, war das Gegenstück zu einer Spalte, die nur an Archivbelegen
    // etwas zeigte — beide sind mit der vereinten Spalte weggefallen.
    //
    // Serverseitig und nicht im Browser: `document.query.ts` setzt ihn über
    // `belegzustandSqlAusdruck()` in die WHERE-Klausel, damit er auf den
    // GANZEN Bestand wirkt und nicht nur auf die geladene Seite.
    belegzustand: belegzustandSchema.optional(),
    /**
     * Sortierung — Auftrag `W-belegsortierung`, 2026-09-10.
     *
     * **Serverseitig und nicht im Browser, und das ist der ganze Punkt.** Die
     * Liste kommt seitenweise (`limit`/`offset`); eine Sortierung, die nur die
     * geladene Seite umordnet, gäbe je nach Blätterstand eine andere Antwort
     * auf dieselbe Frage. Fehlt `sort`, bleibt es bei der bisherigen Ordnung
     * (Kreis, Zählerstand, Anlagezeitpunkt, Entwürfe zuerst) — der
     * Vorgabezustand, in den die dritte Kopfklick zurückführt.
     *
     * `order` ohne `sort` bleibt wirkungslos: Die Vorgabeordnung hat keine
     * Richtung, die sich umdrehen liesse. Ein unbrauchbarer Wert in einem der
     * beiden wird ABGELEHNT und nicht übergangen — anders als bei `type` und
     * `status`, und aus dem Grund, aus dem auch ein unbrauchbarer Stichtag
     * abgelehnt wird: Eine übergangene Sortierung liefert eine vollständige,
     * plausible und falsch geordnete Liste.
     */
    sort: dokumentSortierfeldSchema.optional(),
    order: sortierrichtungSchema.optional(),
    limit: z.number().int().positive().max(500).optional(),
    offset: z.number().int().nonnegative().optional(),
})
    .strict();
// --- Antworten (Belegwesen) -------------------------------------------------
/**
 * Die eingefrorene Kundenanschrift eines Belegs, wie sie nach aussen geht
 * (Phase-2-Spec 5.10) — Träger von `DocumentSummary.billing` und damit von
 * `DocumentView.billing`.
 *
 * **`email` ist seit `CU-belegmail` (2026-09-04, Migration `0157`) dabei** und
 * `nullable`, nicht `optional`: Der Beleg sagt entweder eine Adresse oder
 * ausdrücklich keine. `null` heisst „der Altbestand führte keine oder keine
 * formgerechte Adresse (Befund `email_ungueltig`), oder der Beleg entstand vor
 * `0157` und ist noch nicht nachgetragen" — ein fehlendes Feld hiesse dagegen
 * „diese Fassung der Schnittstelle kennt die Frage nicht", und das wäre eine
 * andere Aussage.
 *
 * Sie steht **nicht** auf dem gedruckten Beleg (Owner-Entscheid 2026-09-05):
 * Weder die Vorlagen noch die Klartextprojektion lesen dieses Feld.
 */
export const billingAddressViewSchema = z
    .object({
    displayCustomerNo: z.string(),
    legalName: z.string(),
    contactName: z.string().nullable(),
    salutation: z.string().nullable(),
    street: z.string(),
    addressExtra: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
    countryCode: z.string(),
    vatId: z.string().nullable(),
    customerType: customerTypeSchema,
    email: z.string().nullable(),
})
    .strict();
/**
 * Eine Belegposition, wie sie nach aussen geht (`document.query.ts`,
 * `DocumentLineView`).
 *
 * **`quantity`, `unitPrice` und `taxRate` bleiben Zeichenketten und werden
 * NICHT zu `Factor`/`Money` transformiert.** So liefert sie die Abfrage: die
 * Werte kommen roh aus der Datenbank (`numeric(15,4)`/`numeric(6,4)` als
 * `::text`), ohne die Skalen- und Vorzeichenprüfung, die `quantityFactor`,
 * `unitPriceAmount` und `taxRateFactor` an der EINGABE vornehmen. Ein
 * Antwortvertrag, der hier transformierte Typen behauptete, sagte eine
 * Prüfung zu, die beim Lesen nicht stattfindet.
 *
 * `netAmount`/`taxAmount`/`grossAmount` sind bereits `Money` (`fromDb` in
 * `document.query.ts`) und dürfen **negativ** sein — die Rabattzeile hat
 * keinen eigenen Vorzeichenschutz (Spec 3.4, `document-arithmetic.ts`); kein
 * `CHECK` verlangt Nichtnegativität. `centAmount` und nicht `nonNegativeAmount`
 * ist deshalb die ehrliche Wahl.
 */
export const documentLineViewSchema = z
    .object({
    id: uuid,
    position: z.number().int().positive(),
    lineType: documentLineTypeSchema,
    description: z.string(),
    servicePeriodStart: isoDate.nullable(),
    servicePeriodEnd: isoDate.nullable(),
    quantity: z.string().nullable(),
    unitCode: z.string().nullable(),
    unitPrice: z.string().nullable(),
    taxRate: z.string().nullable(),
    taxKey: z.string().nullable(),
    revenueAccountNumber: z.string().nullable(),
    netAmount: centAmount,
    taxAmount: centAmount,
    grossAmount: centAmount,
})
    .strict();
/**
 * Eine Steuersatzgruppe, wie sie nach aussen geht (`DocumentTaxGroupView`).
 *
 * `taxRate` bleibt Zeichenkette — dieselbe Begründung wie bei
 * `documentLineViewSchema`. Die Beträge dürfen aus demselben Grund negativ
 * sein: Kein `CHECK` auf `document_tax_group` verlangt Nichtnegativität, und
 * eine Gruppe, die ausschliesslich Rabattzeilen bündelt, wäre es nicht.
 */
export const documentTaxGroupViewSchema = z
    .object({
    taxRate: z.string(),
    taxKey: z.string(),
    netAmount: centAmount,
    taxAmount: centAmount,
    grossAmount: centAmount,
})
    .strict();
/**
 * Ein Beleg in der Liste — ohne Positionen und Steuersatzgruppen, die Liste
 * zeigt sie nicht (`document.query.ts`, `DocumentSummary`).
 *
 * `netTotal`/`taxTotal`/`grossTotal` benutzen `centAmount` und nicht
 * `nonNegativeAmount`: `document_totals_sum` (Migration 0022) verlangt nur
 * `gross_total = net_total + tax_total`, keine Nichtnegativität, und ein
 * Vertrag, der mehr zusagte als die Datenbank durchsetzt, wäre eine erfundene
 * Zusicherung.
 */
/**
 * **Die Mahndaten des Altsystems an einem Archivbeleg**, wie sie nach aussen
 * gehen — Auftrag `AI-mahnaltdaten` (2026-09-05), Migration `0158`.
 *
 * `null` an jedem Beleg ohne Mahnstatus und an jedem Beleg, der kein
 * Archivbeleg ist — `nullable` und nicht `optional`, aus demselben Grund wie
 * bei `billing.email`: Der Beleg sagt entweder Mahndaten oder ausdrücklich
 * keine; ein fehlendes Feld hiesse „diese Fassung der Schnittstelle kennt die
 * Frage nicht".
 *
 * **Herkunftsangaben, keine Buchung** (Auftraggeberentscheid 2026-09-05):
 * `fee` und `interest` sind weder in `grossTotal` enthalten noch in der
 * Journalbuchung des Belegs (`E-841`). Gebucht wird erst bei Zahlung, als
 * nicht steuerbarer sonstiger Ertrag — das ist Sache der kommenden
 * Zahlungszuordnungs-Ära.
 *
 * **`interest` ist ein Schnappschuss zum Tag `interestAsOf`.** Das Altsystem
 * speichert keinen Zinsbetrag, es rechnet ihn live — und zwar gemessen auch
 * an bereits bezahlten Rechnungen weiter. Deshalb steht `paidAt` daneben:
 * Ohne ihn liesse sich ein Zins an einem Beleg mit Altstatus „bezahlt" nicht
 * einordnen.
 */
export const legacyDunningViewSchema = z
    .object({
    /** `fa_rechnungen.mahnung` — gemessen führt der Altbestand dort nur `0` und `1`. */
    level: z.number().int().positive(),
    /** Die Mahnkosten des Altsystems (gemessen 0,00 / 2,50 / 5,00 / 7,50). */
    fee: centAmount,
    /** Der Verzugszinssatz in Prozent, als Text — `'4.12'` oder `'8.12'`. */
    interestRate: z.string(),
    /** Der nachgerechnete Verzugszins zum Tag `interestAsOf`. */
    interest: centAmount,
    /** Der Tag, auf den `interest` gerechnet ist. */
    interestAsOf: isoDate,
    /** `fa_rechnungen.paid_date` — nicht Teil der Zinsrechnung, siehe oben. */
    paidAt: isoDate.nullable(),
})
    .strict();
export const documentSummarySchema = z
    .object({
    id: uuid,
    // `anyDocumentTypeSchema` und nicht `documentTypeSchema`: Ein Archivbeleg
    // SOLL in der Liste stehen (`AI-typwege`). Der Web-Klient prüft jede
    // Antwort gegen genau dieses Schema (`apps/web/src/api/schemas.ts`) — mit
    // den elf Belegarten hier zerbräche die ganze Belegliste an der ersten
    // importierten Altrechnung, nicht nur deren Zeile.
    type: anyDocumentTypeSchema,
    status: documentStatusSchema,
    // Der VEREINTE Anzeigezustand — die eine Spalte «Zustand» für regulären
    // Beleg und Archivbeleg (Auftrag `AI-zustandvereint`, 2026-09-05).
    // Abgeleitet aus `status` und `legacyStatus` über `belegzustandAus()`,
    // NICHT gespeichert. Die Anzeige liest ausschliesslich dieses Feld; die
    // beiden Quellen daneben bleiben für Herkunft und Lebenszyklus.
    belegzustand: belegzustandSchema,
    // Der übernommene Zahlungsstand des Altsystems (`document.legacy_status`,
    // Migration `0119`, `D-2`) — Auftrag `AI-zahlstatusanzeige`. `null` an
    // jedem Beleg, der KEIN Archivbeleg ist; an einem Archivbeleg IMMER
    // gesetzt (`document_legacy_status_scope`, Hausregel dieser Spalte).
    // Benannt über `legacyStatusAnzeigeOderUnbenannt` in `document.query.ts`.
    // `'unbenannt'` ist die nicht werfende Antwort auf einen Code, den die
    // Anzeige nicht kennt (Nachtrag Zug 141). Die Rohzahl selbst steht seit
    // `AI-fehlerhinweis` daneben, siehe {@link legacyStatusCode} — dieses
    // Feld bleibt trotzdem die BENANNTE Form und die, die jeder Aufrufer
    // ausser der Fehlererklärung lesen soll.
    //
    // **Bleibt seit `AI-zustandvereint` als reine HERKUNFTSANGABE.** Die
    // Belegliste zeigt ihn nicht mehr als eigene Spalte und die Belegansicht
    // nicht mehr als eigenes Abzeichen — dort steht er klein als
    // „Altsystem: bezahlt" neben dem vereinten Zustand. Aus dem Vertrag
    // entfernen liesse sich die Angabe nicht, ohne die Auskunft zu
    // verlieren, WELCHEN Stand das Altsystem wirklich führte; sie ist die
    // Zusicherung, die die Archivklasse zu machen hat (`D-2`).
    legacyStatus: legacyDocumentStatusSchema.nullable(),
    /**
     * Der ROHE Zahlencode hinter {@link legacyStatus}
     * (`document.legacy_status`, Migration `0119`, `D-2`) — Auftrag
     * `AI-fehlerhinweis` (2026-09-18, Auftraggeberbefund auf Staging: die
     * Belegliste und -ansicht zeigten den vereinten Zustand `fehler` ohne
     * jede Erklärung, warum).
     *
     * **Existiert ausschliesslich, um `fehler` und `unbenannt` zu erklären,
     * nicht als zweiter, allgemeiner Lesepfad auf die Spalte.**
     * `belegzustandAus()` bildet sowohl den vom Altsystem ausdrücklich als
     * fehlerhaft geführten Code `-1` als auch JEDEN Code, den diese Anzeige
     * nicht benennen kann, auf denselben vereinten Zustand `fehler` ab
     * (Kopfkommentar von {@link belegzustandSchema}) — ohne die Rohzahl kann
     * die Oberfläche weder den Altsystem-Code `-1` nennen noch, bei einem
     * wirklich unbenannten Code, sagen WELCHER das ist.
     *
     * `null` an jedem Beleg, der KEIN Archivbeleg ist — dieselbe Grenze wie
     * bei {@link legacyStatus}. `optional()`, weil ältere Fixturen und
     * andere Aufrufer der Antwort das Feld nicht führen müssen (Muster
     * `offenerRest` weiter unten).
     */
    legacyStatusCode: z.number().int().nullable().optional(),
    number: z.string().nullable(),
    numberRangeKey: z.string().nullable(),
    numberSequence: z.string().nullable(),
    customerId: uuid,
    issuerProfileId: uuid,
    billing: billingAddressViewSchema,
    predecessorId: uuid.nullable(),
    reversesDocumentId: uuid.nullable(),
    cancelledByDocumentId: uuid.nullable(),
    issueDate: isoDate.nullable(),
    serviceDate: isoDate.nullable(),
    /**
     * Der Leistungsmonat (`document.service_month`, Migration `0178`).
     *
     * **Seit `ST-pflicht` in der Ansicht** und nicht mehr über eine eigene
     * Abfrage im Dienst: `assertIssuable` las die Spalte bis dahin einzeln
     * nach, weil sie niemand schrieb. Wer sie erfassbar macht, zieht sie in
     * die Ansicht — sonst zeigte die Maske einen Wert, den sie nicht
     * zurücklesen kann.
     */
    serviceMonth: isoDate.nullable(),
    dueDate: isoDate.nullable(),
    lockedAt: z.string().nullable(),
    issuedBy: uuid.nullable(),
    /**
     * Die Mahndaten des Altsystems ({@link legacyDunningViewSchema}) — `null`
     * an jedem Beleg ohne Mahnstatus (`AI-mahnaltdaten`, `0158`).
     */
    legacyDunning: legacyDunningViewSchema.nullable(),
    /**
     * Der Zahlweg des Altsystems (`document.legacy_pay_method`, Migration
     * `0171`, Auftrag `CU-einloesungen2026`) — `null` an jedem Beleg, der
     * kein Archivbeleg ist, und an jedem Archivbeleg, dessen Alt-Tabelle die
     * Angabe nicht führt (`fa_storno`, `fa_gutschrift`).
     *
     * **Freier Text und kein Enum**, weil die Alt-Spalte freier Text ist: Der
     * Beleg sagt, was dort stand. Die Belegansicht zeigt ihn als
     * „Zahlweg (Altsystem)" und nur dann, wenn er gesetzt ist.
     */
    legacyPayMethod: z.string().nullable(),
    currency: z.string(),
    language: z.string(),
    priceBasis: priceBasisSchema,
    taxScenario: taxScenarioSchema,
    taxScenarioSource: provenanceSchema,
    netTotal: centAmount,
    taxTotal: centAmount,
    grossTotal: centAmount,
    /**
     * Der offene Rest des Belegs — `beleg_offener_rest.offener_rest`
     * (`0161`, fortgeschrieben bis `0223`), dieselbe Sicht, die auch der
     * Zahlungsstand und die Zuordnung nutzen. **Additiv seit
     * `ZZ-vorbelegung` (2026-09-16):** Der Zuordnen-Dialog braucht ihn, um
     * eine neue Position mit dem tatsächlich noch offenen Betrag
     * vorzubelegen statt mit dem vollen Bruttobetrag — bei einer
     * Teilzahlung wäre das sonst mehr, als die Bewegung trägt (Anlass:
     * eine FinTS-Bewegung über 500,00 € auf eine Rechnung über 1.010,31 €).
     * Kann negativ sein (überzahlt). `optional()`, weil ältere Fixturen und
     * andere Aufrufer der Antwort das Feld nicht führen müssen; der Dienst
     * liefert es an jeder Zeile, weil die Sicht für jeden Beleg genau eine
     * Zeile trägt.
     */
    offenerRest: centAmount.optional(),
    requiresApproval: z.boolean(),
    approvedAt: z.string().nullable(),
    approvedBy: uuid.nullable(),
    /**
     * **Die Freigabe war eine Einzelfreigabe** (`D-31`, `0263`): Dieselbe
     * Person hat angelegt (oder ausgestellt) und freigegeben — zulässig nur
     * unter einer Freigaberegel ohne vier Augen. `optional()` aus demselben
     * Grund wie `offenerRest`: Ältere Fixturen führen das Feld nicht; der
     * Dienst liefert es an jedem Beleg.
     */
    einzelfreigabe: z.boolean().optional(),
    sentAt: z.string().nullable(),
    sentBy: uuid.nullable(),
    pdfObjectKey: z.string().nullable(),
    pdfSha256: z.string().nullable(),
    pdfCreatedAt: z.string().nullable(),
    templateKey: z.string().nullable(),
    templateVersion: z.string().nullable(),
    journalEntryId: uuid.nullable(),
    sourceSystem: z.string(),
    sourceReference: z.string().nullable(),
    correlationId: uuid,
    createdAt: z.string(),
    createdBy: uuid,
    updatedAt: z.string(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/documents` — `{ documents: [...], total }`. `total` ist **nicht** die
 * Seitengrösse, sondern ein echtes `COUNT(*)` über dieselben Filter ohne `limit`/`offset`. Der Umschlag lebt neben
 * seinem Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const documentListResponseSchema = z
    .object({ documents: z.array(documentSummarySchema).readonly(), total: z.number().int().nonnegative() })
    .strict();
/**
 * Die zwei Portal-Ereignisarten, die AM BELEG aufgezeichnet werden.
 *
 * **Dieselbe Aufzählung wie `portalKundenereignisArtSchema` in
 * `portal.ts` — seit Befund H8 des Reviews vom 2026-09-17 (`KP-oberflaeche`)
 * buchstäblich dieselbe Objektreferenz, aus `./portal-arten.js` importiert.**
 * Der Grund für das eigene Blattmodul ist die Richtung der bestehenden
 * Importe und kein Versehen: `portal.ts` importiert `unitPriceAmount` aus
 * dieser Datei; ein Import in der Gegenrichtung wäre ein Kreis, und ein
 * Kreis über zwei Moduln, deren Wert beim Laden entsteht (`z.enum(…)` ist
 * ein Aufruf, keine Deklaration), endet in einer uninitialisierten Bindung
 * statt in einem Schema. `portal-arten.ts` importiert nur `zod` und kann
 * deshalb von beiden Seiten gefahrlos importiert werden — vor diesem Befund
 * standen hier zwei wortgleiche Aufzählungen, nur durch eine Testprobe
 * gehalten; die Probe entfällt, weil beide Seiten jetzt dieselbe Referenz
 * benutzen.
 */
export const dokumentPortalEreignisArtSchema = kundenportalEreignisArtSchema;
/**
 * Ein aufgezeichnetes Portal-Ereignis am Beleg (`portal_ereignis`, Migration
 * `0236`).
 *
 * **Ohne `ip` und `userAgent`**, wie die Arbeitsliste `portal_vorgang_offen`:
 * Die Belegseite beantwortet „hat der Kunde den Beleg angesehen" und nicht
 * „von wo" — und die Herkunftsangaben verfallen nach 90 Tagen (`D-22`), eine
 * Anzeige, die sie zeigte, hätte ab Tag 91 eine Leerstelle ohne Erklärung.
 */
export const dokumentPortalEreignisSchema = z
    .object({
    art: dokumentPortalEreignisArtSchema,
    zeitpunkt: z.string(),
})
    .strict();
/**
 * Ein Versuch, die Bestätigung zur Angebotsentscheidung zu verschicken
 * (`angebot_entscheidung_mail`, Migration `0235`).
 *
 * **Alle drei Felder optional, und die Abwesenheit sagt jeweils etwas
 * anderes.** Kein `empfaenger` heisst: Es gab keine Adresse — der Kunde trägt
 * weder `billing_email` noch `customer.email` —, also keinen Versuch und
 * damit weder Erfolg noch Fehler (CHECK
 * `angebot_entscheidung_mail_ohne_empfaenger`). Kein `gesendetAm` bei
 * gesetztem `fehler` heisst: Der Versuch ist gescheitert; kein `fehler` bei
 * gesetztem `gesendetAm`: Er ist durchgekommen. Beides zugleich verhindert
 * der CHECK `angebot_entscheidung_mail_ergebnis` in der Datenbank.
 */
export const dokumentAngebotEntscheidungMailSchema = z
    .object({
    empfaenger: z.string().nullish(),
    gesendetAm: z.string().nullish(),
    fehler: z.string().nullish(),
})
    .strict();
/**
 * Die Entscheidung des Kunden über ein Angebot, wie die Sachbearbeitung sie
 * auf der Belegseite wiedersieht (`angebot_entscheidung`, Migration `0235`).
 *
 * **Ohne `ip` und `userAgent`, anders als die Tabelle.** Dort sind sie der
 * Nachweis einer Willenserklärung und bleiben zehn Jahre (§ 147 AO); auf
 * einem Bildschirm, den jede Sachbearbeitung öffnet, sind sie eine
 * personenbezogene Angabe ohne Zweck. Wer den Nachweis braucht, liest die
 * Zeile.
 *
 * **`bestellnummer` und `grund` schliessen sich nicht aus** — die Bestellnummer
 * steht an einer Annahme, der Grund an einer Ablehnung, und beide sind
 * freiwillig: Ein Kunde, der ohne Begründung ablehnt, hat trotzdem
 * abgelehnt (`D-3`). `agbFassung` und `anmerkung` der Tabelle stehen hier
 * ausdrücklich NICHT: Jene ist ein Nachweis für den Streitfall, diese die
 * Prosa des Kunden — die Rückfrage ist der Weg, auf dem Prosa vor einen
 * Menschen kommt (`portal_vorgang`), und nicht ein Feld in der Kopfzeile
 * eines Belegs.
 *
 * **`art` als Import von `kundenportalEntscheidungArtSchema`** aus
 * `./portal-arten.js` — seit Befund H8 des Reviews vom 2026-09-17
 * (`KP-oberflaeche`) dieselbe Objektreferenz wie `portalEntscheidungArtSchema`
 * in `portal.ts`, aus demselben Grund wie bei
 * {@link dokumentPortalEreignisArtSchema}: Vorher stand hier eine eigene,
 * wortgleiche `z.enum(...)`-Anweisung, weil ein direkter Import aus
 * `portal.ts` einen Modulkreis ergeben hätte (`portal.ts` importiert
 * `unitPriceAmount` aus dieser Datei).
 */
export const dokumentAngebotEntscheidungSchema = z
    .object({
    art: kundenportalEntscheidungArtSchema,
    zeitpunkt: z.string(),
    bestellnummer: z.string().nullish(),
    grund: z.string().nullish(),
    /**
     * Jeder Versuch, je Empfänger eine Zeile — leer heisst: Es wurde noch
     * kein Versand protokolliert. Eine leere Liste und kein `optional`:
     * „keine Zeile" ist eine Tatsache über den Versand, und kein fehlendes
     * Wissen.
     */
    mails: z.array(dokumentAngebotEntscheidungMailSchema),
})
    .strict();
/**
 * Ein Beleg samt Positionen und Steuersatzgruppen (`DocumentView`).
 *
 * **`portalToken` und `portalLink` stehen hier und NICHT in
 * `documentSummarySchema`** (Kundenportal-Spec Abschnitt 2, `E-1410`). Der
 * Token ist ein Zugriffsschlüssel: Wer ihn hat, sieht den Beleg ohne
 * Anmeldung. Er gehört deshalb in die eine Antwort, die ein angemeldeter
 * Mensch für EINEN Beleg abruft — und nicht in die Belegliste, nicht in einen
 * Suchtreffer und nicht in einen Webhook-Rumpf, die alle vielfach und
 * unbeaufsichtigt weitergereicht werden. Dass die Erweiterung hier steht und
 * nicht im Grundschema, ist genau diese Grenze, in einer Zeile.
 */
export const documentViewSchema = documentSummarySchema.extend({
    // `.readonly()` auf beiden Listen: `document.query.ts` liefert `readonly
    // DocumentLineView[]`/`readonly DocumentTaxGroupView[]`, und ein mutables
    // Array liesse den Typvergleich am Controller-Rückgabetyp scheitern —
    // dieselbe Falle wie bei `sprossen` in `eingangsrechnungen.ts`.
    lines: z.array(documentLineViewSchema).readonly(),
    taxGroups: z.array(documentTaxGroupViewSchema).readonly(),
    /** `document.portal_token` (Migration `0234`) — 43 Zeichen base64url. */
    portalToken: z.string(),
    /**
     * Der fertige Link, den die Sachbearbeitung kopiert: `PORTAL_BASE_URL` +
     * `/r/` + Token (`D-17`).
     *
     * **Im Server gebildet und nicht in der Oberfläche zusammengesetzt**: Die
     * Basisadresse ist eine Betriebseinstellung, und eine Oberfläche, die sie
     * kennen müsste, trüge sie in ihrem Bündel — dort, wo sie niemand mehr
     * ändern kann, ohne neu zu bauen.
     *
     * **Leer, wenn die Installation keine Basisadresse gesetzt hat**
     * (`PF-marke`, `E-1657`): Es gibt keine Vorgabeadresse, und ein Link mit
     * einer erfundenen Adresse wäre schlimmer als keiner. Die Oberfläche zeigt
     * bei leerem Wert keinen Link.
     */
    portalLink: z.string(),
    /**
     * Die aufgezeichneten Portal-Ereignisse zu diesem Beleg, neueste zuerst
     * (`portal_ereignis`, Migration `0236`).
     *
     * **Optional, weil die Antwort sie füllt und nicht dieses Schema.** Eine
     * Belegantwort, die noch keine Ereignisse mitbringt, ist etwas anderes als
     * eine, die keine hat — und eine leere Liste zu verlangen hiesse, jede
     * Antwort zu einer Aussage über den Portalbestand zu machen, auch die, die
     * ihn gar nicht abgefragt hat.
     */
    portalEreignisse: z.array(dokumentPortalEreignisSchema).optional(),
    /**
     * Die Entscheidung des Kunden über dieses Angebot, samt dem Zustand der
     * Bestätigungsmails — fehlt an jedem Beleg, der kein Angebot ist, und an
     * jedem Angebot, über das noch niemand entschieden hat (`D-3`).
     */
    angebotEntscheidung: dokumentAngebotEntscheidungSchema.optional(),
    /**
     * `true`, wenn `document.altlink_hash_sha256` gesetzt ist — der Altlink
     * dieses Archivbelegs löst im Portal noch auf (Abschnitt 3, `D-2`).
     *
     * **Ein Wahrheitswert und nicht der Hash.** Der Hash ist der Schlüssel,
     * mit dem ein Altlink aufgelöst wird; ihn in eine Belegantwort zu legen,
     * die jede Sachbearbeitung öffnet, wäre eine Kopie des Zugriffsweges an
     * einem Ort, der nicht dafür gebaut ist — dieselbe Grenze, die
     * `E-1385` für die Logzeilen zieht.
     */
    altlinkHashGesetzt: z.boolean().optional(),
});
/** Ein Beleg ohne Archivstück — die Sicht `document_without_pdf`. */
export const documentWithoutPdfViewSchema = z
    .object({
    id: uuid,
    number: z.string().nullable(),
    // Vierzehn: Die Sicht `document_without_pdf` kennt keinen Typfilter, und
    // ein Archivbeleg ohne Archivstück ist genau der Betriebsbefund, den sie
    // zeigen soll (`AI-pdf` legt es ab).
    type: anyDocumentTypeSchema,
    issueDate: isoDate.nullable(),
    lockedAt: z.string().nullable(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/documents/without-pdf` — `{ documents: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const documentWithoutPdfListResponseSchema = z.object({ documents: z.array(documentWithoutPdfViewSchema).readonly() }).strict();
/**
 * Eine Zeile des Lückenberichts (`NumberRangeReportRow`, Sicht
 * `number_range_gap_report`).
 *
 * Jede Zahl ist eine Zeichenkette: `counter.current_value` ist `bigint`, und
 * `count(*)`/`sum()` in PostgreSQL ebenso — durch eine `number` geführt
 * verlöre sie ab 2^53 stillschweigend Genauigkeit (Spec 5.5).
 */
export const numberRangeReportRowSchema = z
    .object({
    rangeKey: z.string(),
    gapless: z.boolean(),
    zaehlerstand: z.string(),
    belege: z.string(),
    begruendeteLuecken: z.string(),
    erwartet: z.string(),
    unerklaert: z.string(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/documents/number-ranges` — `{ ranges: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const numberRangeReportListResponseSchema = z.object({ ranges: z.array(numberRangeReportRowSchema).readonly() }).strict();
/** Eine begründete Nummernlücke. Der Eintrag ist unveränderbar (Spec 8.8). */
export const numberGapViewSchema = z
    .object({
    id: uuid,
    rangeKey: z.string(),
    sequenceFrom: z.string(),
    sequenceTo: z.string(),
    reason: z.string(),
    note: z.string().nullable(),
    createdAt: z.string(),
    createdBy: uuid,
})
    .strict();
/** Die Aufzählung der fehlenden Zählerstände eines Kreises (`NumberGapDetail`). */
export const numberGapDetailSchema = z
    .object({
    rangeKey: z.string(),
    from: z.string(),
    to: z.string(),
    // `.readonly()`: `NumberGapService.detail()` liefert beide Listen als
    // `readonly …[]` (`number-gap.service.ts`).
    missing: z.array(z.string()).readonly(),
    explained: z.array(numberGapViewSchema).readonly(),
})
    .strict();
/**
 * Was die Ausstellung zurückmeldet (`IssueResult`).
 *
 * `alreadyIssued` ist der Unterschied zwischen „gerade ausgestellt" und „war
 * es schon" — ein zweiter Aufruf liefert 200 und nicht 409 (Spec 8.3).
 *
 * `hinweise` — Lastschrift-Spec
 * (`docs/superpowers/specs/2026-09-15-lastschrift-design.md`, Abschnitt 4,
 * `D-10`): benannte Hinweise, die **keinen** Fehler auslösen und die
 * Rechnung trotzdem ausstellen lassen. Zwei mögliche Werte, beide nur bei
 * einer Rechnung mit aktivem Lastschriftmandat: `LASTSCHRIFT_GLAEUBIGER_ID_FEHLT`
 * (das Ausstellerprofil trägt keine SEPA-Gläubiger-ID; die Rechnung zeigt
 * den Überweisungsblock) und `lastschrift_frist_kurz` (`E-1351`, das
 * Einzugsdatum liegt weniger als 14 Kalendertage nach dem
 * Ausstellungsdatum). Leer im Regelfall.
 */
export const issueResultSchema = z
    .object({
    document: documentViewSchema,
    alreadyIssued: z.boolean(),
    hinweise: z.array(z.string()),
})
    .strict();
/**
 * Eine Position, deren Einzelpreis den Basiswechsel nicht exakt übersteht
 * (`PriceBasisWarning`). `unitPriceAmount` und nicht `centAmount`: Der
 * Einzelpreis trägt bis zu vier Nachkommastellen (Spec 4.2), derselbe
 * Baustein wie an der Eingabe.
 */
export const priceBasisWarningSchema = z
    .object({
    position: z.number().int().positive(),
    description: z.string(),
    unitPrice: unitPriceAmount,
    roundTrip: unitPriceAmount,
})
    .strict();
/**
 * Was der Basiswechsel zurückmeldet: der neue Beleg und seine Warnungen
 * (`PriceBasisChangeResult`).
 *
 * `priceBasisChangeResultSchema` und nicht `priceBasisChangeSchema` — dieser
 * Name ist bereits die EINGABE des Basiswechsels (`basis`/`mode`, oben in
 * dieser Datei). Zwei Verträge mit demselben Komponentennamen wiesen
 * `benenneVertraege()` im Erzeuger ab (`apps/api/src/openapi/dokument.ts`).
 */
export const priceBasisChangeResultSchema = z
    .object({
    document: documentViewSchema,
    // `.readonly()`: `computePriceBasisChange()` liefert `readonly
    // PriceBasisWarning[]` (`document.service.ts`).
    warnings: z.array(priceBasisWarningSchema).readonly(),
})
    .strict();
/**
 * Der Betrag einer Steuersatzgruppe, wie ihn die Antwort trägt.
 *
 * `nonNegativeAmount` steht hier als **Baustein für die Antwortprüfung im
 * Frontend** und wird von den Eingabeschemas nicht gebraucht: Die Beträge einer
 * Position rechnet der Dienst, der Aufrufer schickt sie nicht.
 */
export const taxGroupAmountSchema = z
    .object({
    taxRate: taxRateFactor,
    taxKey: pflichtText('taxKey'),
    net: nonNegativeAmount,
    tax: nonNegativeAmount,
    gross: nonNegativeAmount,
})
    .strict();
//# sourceMappingURL=documents.js.map